import React from 'react';

interface VotingResultsProps {
  results: {
    totalVotes: number;
    yes: number;
    no: number;
    abstain: number;
    byParish?: Array<{
      parish: string;
      yes: number;
      no: number;
      abstain: number;
    }>;
  };
  voteDetails?: Array<{
    id: string;
    option: 'yes' | 'no' | 'abstain';
    userId?: {
      _id: string;
      displayName: string;
      email: string;
    };
    castAt: string;
  }>;
  isAnonymous: boolean;
  className?: string;
}

const VotingResults: React.FC<VotingResultsProps> = ({
  results,
  voteDetails,
  isAnonymous,
  className = '',
}) => {
  const total = results.totalVotes;
  const yesPercent = total > 0 ? ((results.yes / total) * 100).toFixed(1) : '0';
  const noPercent = total > 0 ? ((results.no / total) * 100).toFixed(1) : '0';
  const abstainPercent = total > 0 ? ((results.abstain / total) * 100).toFixed(1) : '0';

  return (
    <div className={`space-y-4 ${className}`}>
      <h4 className="font-semibold text-lg">Results</h4>

      {/* Summary */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[var(--color-success-500)]"></div>
            <span>Yes</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-32 bg-[var(--color-neutral-200)] rounded-full h-2">
              <div
                className="bg-[var(--color-success-500)] h-2 rounded-full"
                style={{ width: `${yesPercent}%` }}
              ></div>
            </div>
            <span className="font-medium w-16 text-right">
              {results.yes} ({yesPercent}%)
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[var(--color-error-500)]"></div>
            <span>No</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-32 bg-[var(--color-neutral-200)] rounded-full h-2">
              <div
                className="bg-[var(--color-error-500)] h-2 rounded-full"
                style={{ width: `${noPercent}%` }}
              ></div>
            </div>
            <span className="font-medium w-16 text-right">
              {results.no} ({noPercent}%)
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[var(--color-neutral-400)]"></div>
            <span>Abstain</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-32 bg-[var(--color-neutral-200)] rounded-full h-2">
              <div
                className="bg-[var(--color-neutral-400)] h-2 rounded-full"
                style={{ width: `${abstainPercent}%` }}
              ></div>
            </div>
            <span className="font-medium w-16 text-right">
              {results.abstain} ({abstainPercent}%)
            </span>
          </div>
        </div>

        <div className="pt-2 border-t border-[var(--color-neutral-border)] flex justify-between font-semibold">
          <span>Total Votes:</span>
          <span>{total}</span>
        </div>
      </div>

      {/* By Parish Breakdown */}
      {results.byParish && results.byParish.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--color-neutral-border)]">
          <h5 className="font-medium mb-2 text-[var(--color-default-font)]">Results by Parish</h5>
          <div className="space-y-2">
            {results.byParish.map((parish, index) => (
              <div key={index} className="text-sm">
                <div className="font-medium text-[var(--color-default-font)] mb-1">{parish.parish}</div>
                <div className="flex gap-4 text-xs text-[var(--color-subtext-color)]">
                  <span>Yes: {parish.yes}</span>
                  <span>No: {parish.no}</span>
                  <span>Abstain: {parish.abstain}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vote Details (only for non-anonymous sessions) */}
      {!isAnonymous && voteDetails && voteDetails.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--color-neutral-border)]">
          <h5 className="font-medium mb-2 text-[var(--color-default-font)]">Recent Votes</h5>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {voteDetails.map((vote) => (
              <div
                key={vote.id}
                className="flex items-center justify-between p-2 bg-[var(--color-neutral-50)] rounded text-sm"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      vote.option === 'yes'
                        ? 'bg-[var(--color-success-100)] text-[var(--color-success-700)]'
                        : vote.option === 'no'
                        ? 'bg-[var(--color-error-100)] text-[var(--color-error-700)]'
                        : 'bg-[var(--color-neutral-100)] text-[var(--color-neutral-700)]'
                    }`}
                  >
                    {vote.option.toUpperCase()}
                  </span>
                  {vote.userId && (
                    <span className="text-[var(--color-default-font)]">{vote.userId.displayName}</span>
                  )}
                </div>
                <span className="text-[var(--color-subtext-color)] text-xs">
                  {new Date(vote.castAt).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VotingResults;

